import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, MapPin, Calendar, Building2, Search, Clock, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Election countdown hook
const useElectionCountdown = (electionDate) => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    if (!electionDate) return;
    
    const targetDate = new Date(electionDate);
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [electionDate]);
  
  return countdown;
};

const PARTY_COLORS = {
  labour: { bg: 'bg-red-500', text: 'text-red-500', label: 'Labour' },
  conservative: { bg: 'bg-blue-600', text: 'text-blue-600', label: 'Conservative' },
  libdem: { bg: 'bg-orange-500', text: 'text-orange-500', label: 'Lib Dem' },
  green: { bg: 'bg-green-500', text: 'text-green-500', label: 'Green' },
  independent: { bg: 'bg-gray-500', text: 'text-gray-500', label: 'Independent' },
  reform: { bg: 'bg-cyan-500', text: 'text-cyan-500', label: 'Reform UK' },
  snp: { bg: 'bg-yellow-500', text: 'text-yellow-500', label: 'SNP' },
  plaid_cymru: { bg: 'bg-emerald-600', text: 'text-emerald-600', label: 'Plaid Cymru' },
  dup: { bg: 'bg-red-800', text: 'text-red-800', label: 'DUP' },
  sinn_fein: { bg: 'bg-green-700', text: 'text-green-700', label: 'Sinn Féin' },
  sdlp: { bg: 'bg-green-600', text: 'text-green-600', label: 'SDLP' },
  alliance: { bg: 'bg-amber-500', text: 'text-amber-500', label: 'Alliance' },
  uup: { bg: 'bg-blue-700', text: 'text-blue-700', label: 'UUP' },
  tuv: { bg: 'bg-indigo-700', text: 'text-indigo-700', label: 'TUV' },
  vacant: { bg: 'bg-slate-300', text: 'text-slate-300', label: 'Vacant' },
  other: { bg: 'bg-slate-400', text: 'text-slate-400', label: 'Other' }
};

// Election Countdown Card Component
const ElectionCountdownCard = ({ electionDate, electionType, councilName }) => {
  const countdown = useElectionCountdown(electionDate);
  const formattedDate = new Date(electionDate).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  const electionTypeLabel = electionType === 'by-thirds' 
    ? '1/3 of seats contested' 
    : electionType === 'by-halves' 
      ? '1/2 of seats contested' 
      : 'All seats contested';
  
  const isUrgent = countdown.days <= 30;
  
  return (
    <div 
      className={`mb-6 rounded-2xl overflow-hidden shadow-lg ${
        isUrgent 
          ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500' 
          : 'bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600'
      }`}
      data-testid="election-countdown-card"
    >
      <div className="p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5" />
              <span className="text-sm font-bold uppercase tracking-wider opacity-80">
                Next Election
              </span>
              {isUrgent && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold animate-pulse">
                  COMING SOON
                </span>
              )}
            </div>
            <h2 className="text-2xl md:text-3xl font-black mb-1">{councilName}</h2>
            <p className="opacity-90 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formattedDate}
            </p>
            <p className="text-sm opacity-75 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {electionTypeLabel}
            </p>
          </div>
        </div>
        
        {/* Countdown Grid */}
        <div className="grid grid-cols-4 gap-3 mt-6">
          {[
            { value: countdown.days, label: 'Days' },
            { value: countdown.hours, label: 'Hours' },
            { value: countdown.minutes, label: 'Mins' },
            { value: countdown.seconds, label: 'Secs' }
          ].map((item) => (
            <div key={item.label} className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <div className="text-3xl md:text-4xl font-black tabular-nums">
                {String(item.value).padStart(2, '0')}
              </div>
              <div className="text-xs uppercase tracking-wider opacity-80">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CouncillorCard = ({ councillor }) => {
  const colors = PARTY_COLORS[councillor.party] || PARTY_COLORS.other;
  const slug = councillor.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
  return (
    <Link 
      to={`/politician/${slug}`}
      className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md hover:border-slate-300 transition-all block group"
    >
      <div className="flex items-start gap-2">
        <div className={`w-1 h-full min-h-[40px] rounded-full ${colors.bg}`} />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-slate-900 text-sm truncate group-hover:text-blue-600 transition-colors">{councillor.name}</h4>
          <p className="text-xs text-slate-500 truncate">Ward: {councillor.ward}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${colors.bg} text-white font-medium`}>
              {colors.label}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default function CouncilDetailPage() {
  const { councilName } = useParams();
  const [council, setCouncil] = useState(null);
  const [councillors, setCouncillors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParty, setSelectedParty] = useState('');

  useEffect(() => {
    fetchCouncilData();
  }, [councilName]);

  const fetchCouncilData = async () => {
    try {
      setLoading(true);
      const decodedName = decodeURIComponent(councilName);
      
      // Fetch council details
      const councilRes = await axios.get(`${API}/councils`);
      const foundCouncil = councilRes.data.councils?.find(
        c => c.name.toLowerCase() === decodedName.toLowerCase()
      );
      
      if (foundCouncil) {
        setCouncil(foundCouncil);
      }
      
      // Fetch councillors for this council
      const councillorsRes = await axios.get(`${API}/councillors`, {
        params: { search: decodedName, limit: 500 }
      });
      
      // Filter to only councillors from this exact council
      const filteredCouncillors = (councillorsRes.data.councillors || []).filter(
        c => c.council?.toLowerCase() === decodedName.toLowerCase() ||
             c.council_name?.toLowerCase() === decodedName.toLowerCase()
      );
      
      setCouncillors(filteredCouncillors);
    } catch (error) {
      console.error('Error fetching council data:', error);
      toast.error('Failed to load council data');
    } finally {
      setLoading(false);
    }
  };

  // Filter councillors
  const filteredCouncillors = councillors.filter(c => {
    const matchesSearch = !searchQuery || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.ward?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesParty = !selectedParty || c.party === selectedParty;
    return matchesSearch && matchesParty;
  });

  // Group councillors by ward
  const councillorsByWard = filteredCouncillors.reduce((acc, c) => {
    const ward = c.ward || 'Unknown Ward';
    if (!acc[ward]) acc[ward] = [];
    acc[ward].push(c);
    return acc;
  }, {});

  // Get unique parties for filter
  const parties = [...new Set(councillors.map(c => c.party))].sort();

  // Calculate party breakdown
  const partyBreakdown = councillors.reduce((acc, c) => {
    acc[c.party] = (acc[c.party] || 0) + 1;
    return acc;
  }, {});

  const totalSeats = councillors.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/elections" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-2">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Elections</span>
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">{decodeURIComponent(councilName)}</h1>
          {council && (
            <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
              <MapPin className="h-4 w-4" />
              {council.region || 'United Kingdom'}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Election Countdown Card */}
        {council?.next_election && (
          <ElectionCountdownCard 
            electionDate={council.next_election} 
            electionType={council.election_type}
            councilName={decodeURIComponent(councilName)}
          />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Users className="h-6 w-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-900">{totalSeats}</p>
            <p className="text-xs text-slate-500">Total Councillors</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Building2 className="h-6 w-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-900">{Object.keys(councillorsByWard).length}</p>
            <p className="text-xs text-slate-500">Wards</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className={`h-6 w-6 rounded-full mx-auto mb-2 ${PARTY_COLORS[Object.entries(partyBreakdown).sort((a,b) => b[1]-a[1])[0]?.[0]]?.bg || 'bg-slate-400'}`} />
            <p className="text-lg font-bold text-slate-900">
              {PARTY_COLORS[Object.entries(partyBreakdown).sort((a,b) => b[1]-a[1])[0]?.[0]]?.label || 'N/A'}
            </p>
            <p className="text-xs text-slate-500">Largest Party</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Calendar className="h-6 w-6 text-orange-500 mx-auto mb-2" />
            <p className="text-lg font-bold text-slate-900">
              {council?.next_election ? new Date(council.next_election).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBC'}
            </p>
            <p className="text-xs text-slate-500">Next Election</p>
          </div>
        </div>

        {/* Party Breakdown */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
          <h3 className="font-semibold mb-3">Party Composition</h3>
          
          {/* Progress Bar */}
          <div className="h-6 rounded-full overflow-hidden flex bg-slate-100 mb-3">
            {Object.entries(partyBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([party, count]) => {
                const percentage = (count / totalSeats) * 100;
                const colors = PARTY_COLORS[party] || PARTY_COLORS.other;
                return (
                  <div
                    key={party}
                    className={`${colors.bg} h-full`}
                    style={{ width: `${percentage}%` }}
                    title={`${colors.label}: ${count}`}
                  />
                );
              })}
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-3">
            {Object.entries(partyBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([party, count]) => {
                const colors = PARTY_COLORS[party] || PARTY_COLORS.other;
                return (
                  <button
                    key={party}
                    onClick={() => setSelectedParty(selectedParty === party ? '' : party)}
                    className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full transition-all ${
                      selectedParty === party ? 'ring-2 ring-offset-1 ring-blue-500' : ''
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${colors.bg}`} />
                    <span className="font-medium">{colors.label}:</span>
                    <span>{count}</span>
                  </button>
                );
              })}
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search councillors by name or ward..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Councillors by Ward */}
        <div className="space-y-6">
          {Object.entries(councillorsByWard)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([ward, wardCouncillors]) => (
              <div key={ward} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">{ward}</h3>
                  <p className="text-xs text-slate-500">{wardCouncillors.length} councillor{wardCouncillors.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {wardCouncillors.map((councillor, index) => (
                    <CouncillorCard key={councillor.id || index} councillor={councillor} />
                  ))}
                </div>
              </div>
            ))}
        </div>

        {filteredCouncillors.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No councillors found</p>
            {searchQuery && <p className="text-sm mt-2">Try adjusting your search</p>}
          </div>
        )}
      </div>
    </div>
  );
}
